import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  MenuItem,
  Typography,
  Container,
  Paper,
  Modal,
  Tooltip,
  Snackbar,
  Alert,
  IconButton,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import 'react-clock/dist/Clock.css';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import SaveIcon from "@mui/icons-material/Save";

// Add this style tag at the bottom of your component file, or create a separate CSS file
const styles = `
  .custom-time-picker {
    width: 100%;
  }
  .custom-time-picker .react-time-picker__wrapper {
    border: 1px solid rgba(0, 0, 0, 0.23);
    border-radius: 4px;
    padding: 16.5px 14px;
    width: 100%;
  }
  .custom-time-picker .react-time-picker__wrapper:hover {
    border-color: rgba(0, 0, 0, 0.87);
  }
`;



const AddPeriod = () => {
const [formValues, setFormValues] = useState({
  startTime: "",
  endTime: "",
  periodSequence: "",
});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // State for Info Modal
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  // Convert 24-hour time (from input) to "07:00AM" format
 const to12HourFormat = (time24) => {
  if (!time24) return "";
  const [hours, minutes] = time24.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;
  return `${hours12.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}${period}`;
};


  const handleAdd = async () => {
    const payload = {
      startTime: to12HourFormat(formValues.startTime),
      endTime: to12HourFormat(formValues.endTime),
      sequenceNo: parseInt(formValues.periodSequence),
    };

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/TimeTable/CreatePeriod",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.msg || "Failed to add period");
      }

      if (
        data.msg ===
        "Period Sequence Already exist, please try with another one!"
      ) {
        setError(data.msg);
        return;
      }

      setShowSuccessModal(true);
      setFormValues({ startTime: "", endTime: "", periodSequence: "" });
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Info sections data for the Info Modal
  const infoSections = [
    {
      icon: <AccessTimeIcon sx={{ color: "#1976d2", fontSize: 22 }} />,
      title: "Time Fields",
      color: "#e3f2fd",
      borderColor: "#1976d2",
      items: [
        { label: "Start Time", desc: "Pick the time at which the period begins using the time picker. Supports AM/PM format." },
        { label: "End Time", desc: "Pick the time at which the period ends using the time picker. Supports AM/PM format." },
        { label: "Time Format", desc: "Times are internally converted to 12-hour format (e.g. 07:00AM, 01:00PM) before being saved to the server." },
      ],
    },
    {
      icon: <FormatListNumberedIcon sx={{ color: "#7b1fa2", fontSize: 22 }} />,
      title: "Period Sequence",
      color: "#f3e5f5",
      borderColor: "#7b1fa2",
      items: [
        { label: "Period Sequence", desc: "Select the sequence number (1–10) that determines the order of this period in the daily timetable." },
        { label: "Unique Sequence", desc: "Each sequence number must be unique. If the chosen sequence already exists, an error message will be shown and the period will not be saved." },
      ],
    },
    {
      icon: <SaveIcon sx={{ color: "#2e7d32", fontSize: 22 }} />,
      title: "Actions",
      color: "#e8f5e9",
      borderColor: "#2e7d32",
      items: [
        { label: "SAVE", desc: "Submits the form and creates the new period. The button is disabled until all three fields are filled in." },
        { label: "CANCEL", desc: "Discards all changes and navigates back to the previous page without saving." },
        { label: "Validation", desc: "All fields (Start Time, End Time, Period Sequence) are required. The SAVE button remains disabled until all are provided." },
      ],
    },
  ];

  return (
    // Add this in your component's return, before the Container:
    <Container>
<style>{styles}</style>
      <Box
        sx={{
          backgroundColor: "#0B1F40",
          padding: "12px 24px",
          borderRadius: "4px 4px 0 0",
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: 'flex-start',
gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>          Add New Period
        </Typography>

        {/* Info Button */}
        <Tooltip title="View page information & guide" arrow placement="left">
          <IconButton
            onClick={() => setInfoModalOpen(true)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.75)",
              backgroundColor: "rgba(255,255,255,0.1)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              borderRadius: "50%",
              width: 32,
              height: 32,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#1976d2",
                borderColor: "#64B5F6",
                color: "#ffffff",
                boxShadow: "0 0 0 3px rgba(100,181,246,0.25)",
                transform: "scale(1.1)",
              },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper elevation={3} sx={{ p: 4, pt: 6 }}>
       <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
  <Box sx={{ width: '100%' }}>
    <Typography sx={{ mb: 1, fontSize: '12px', color: 'rgba(0,0,0,0.6)' }}>
      Start Time
    </Typography>
    <TimePicker
      onChange={(value) => setFormValues((prev) => ({ ...prev, startTime: value }))}
      value={formValues.startTime}
      disableClock={true}
      format="h:mm a"
      className="custom-time-picker"
    />
  </Box>
  
  <Box sx={{ width: '100%' }}>
    <Typography sx={{ mb: 1, fontSize: '12px', color: 'rgba(0,0,0,0.6)' }}>
      End Time
    </Typography>
    <TimePicker
      onChange={(value) => setFormValues((prev) => ({ ...prev, endTime: value }))}
      value={formValues.endTime}
      disableClock={true}
      format="h:mm a"
      className="custom-time-picker"
    />
  </Box>
</Box>

        <TextField
          select
          label="Period Sequence"
          name="periodSequence"
          value={formValues.periodSequence}
          onChange={handleInputChange}
          fullWidth
          sx={{ mb: 3 }}
        >
          {[...Array(13)].map((_, i) => (
            <MenuItem key={i + 1} value={i + 1}>
              {i + 1}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Tooltip
            title={
              !formValues.startTime ||
              !formValues.endTime ||
              !formValues.periodSequence
                ? "Fill all fields"
                : ""
            }
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAdd}
                disabled={
                  loading ||
                  !formValues.startTime ||
                  !formValues.endTime ||
                  !formValues.periodSequence
                }
                sx={{ width: 100, height: 50, fontWeight: "bold" }}
              >
                {loading ? "SAVING..." : "SAVE"}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            disabled={loading}
            sx={{ width: 100, height: 50, fontWeight: "bold" }}
          >
            CANCEL
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError("")}
      >
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>

      <Modal open={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
          }}
        >
          <Box sx={{ bgcolor: "#0B1F40", p: 2 }}>
            <Typography variant="h6" color="white">
              Period Added
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography>The period has been added successfully.</Typography>
            <Box sx={{ mt: 3, textAlign: "right" }}>
              <Button
                variant="contained"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Info Modal */}
      <Modal
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95vw", sm: 580, md: 620 },
            maxHeight: "85vh",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: "0 24px 64px rgba(11,31,61,0.22)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Info Modal Header */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #0B1F40 0%, #1565C0 100%)",
              padding: "16px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.15)",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <InfoOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  id="info-modal-title"
                  variant="h6"
                  sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}
                >
                  Add New Period — Guide
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                  Complete reference for all fields and actions
                </Typography>
              </Box>
            </Box>
           
          </Box>

          {/* Info Modal Scrollable Body */}
          <Box sx={{ overflowY: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>

            {/* Page Overview */}
            <Box
              sx={{
                backgroundColor: "#f0f4ff",
                border: "1px solid #c5d3f0",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#1a237e", lineHeight: 1.7 }}>
                The <strong>Add New Period</strong> page lets you create a timetable period by specifying its start time, end time, and sequence number. Each period slot is used when building the school's daily timetable.
              </Typography>
            </Box>

            {/* Sections */}
            {infoSections.map((section, sIdx) => (
              <Box
                key={sIdx}
                sx={{
                  backgroundColor: section.color,
                  border: `1px solid ${section.borderColor}30`,
                  borderLeft: `4px solid ${section.borderColor}`,
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  {section.icon}
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1a1a2e", fontSize: "0.95rem" }}>
                    {section.title}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                  {section.items.map((item, iIdx) => (
                    <Box key={iIdx} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <Chip
                        label={item.label}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          height: 22,
                          flexShrink: 0,
                          backgroundColor: `${section.borderColor}18`,
                          color: section.borderColor,
                          border: `1px solid ${section.borderColor}40`,
                          mt: "1px",
                        }}
                      />
                      <Typography variant="body2" sx={{ color: "#424242", lineHeight: 1.5, fontSize: "0.82rem" }}>
                        {item.desc}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}

            {/* Tip Box */}
            <Box
              sx={{
                backgroundColor: "#fffde7",
                border: "1px solid #f9a825",
                borderRadius: 2,
                p: 2,
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
              }}
            >
              <Typography sx={{ fontSize: "1.1rem", mt: "1px" }}>💡</Typography>
              <Typography variant="body2" sx={{ color: "#5d4037", lineHeight: 1.65, fontSize: "0.82rem" }}>
                <strong>Pro Tip:</strong> Plan your period sequence numbers in advance before adding them. Sequence numbers must be unique — if you try to reuse one, the system will reject it and show an error.
              </Typography>
            </Box>

          </Box>

          {/* Info Modal Footer */}
          <Box
            sx={{
              borderTop: "1px solid #e0e0e0",
              px: 2.5,
              py: 1.5,
              display: "flex",
              justifyContent: "flex-end",
              flexShrink: 0,
              backgroundColor: "#fafafa",
            }}
          >
            <Button
              onClick={() => setInfoModalOpen(false)}
              variant="contained"
              color="primary"
              sx={{ fontWeight: 600, px: 3 }}
            >
              Got It
            </Button>
          </Box>
        </Box>
      </Modal>

    </Container>
  );
};

export default AddPeriod;